/**
 * What a build dispatch does with what the readiness gate saw (SCH-2318), said once.
 *
 * The backend's trigger-build use case acts on this, and the dashboard describes it, so the two
 * cannot disagree. They did: the dashboard treated a readiness report it could not read as ready
 * and told the operator "the build is not held back by it", while trigger-build refused that same
 * production build.
 *
 * `BuildVariant` is defined in `./index`, which re-exports this file, so it is taken with
 * `import type` (erased at build time, no cycle).
 */

import type { BuildVariant } from './index'

/**
 * - `dispatch`: every platform is ready; the build goes.
 * - `dispatch-with-warnings`: readiness failed, but this variant is never held back. The failures
 *   come back on the response as warnings.
 * - `dispatch-overridden`: a production build readiness would refuse, sent anyway by a super-admin
 *   with a reason. The bypassed checks are recorded on the dispatch.
 * - `refused`: a production build with failing or unreadable readiness, no override. Nothing is
 *   dispatched or recorded.
 */
export type BuildGateDecision = 'dispatch' | 'dispatch-with-warnings' | 'dispatch-overridden' | 'refused'

/**
 * The readiness policy. Only a PRODUCTION build is held back: it is the binary a store receives.
 * A development, preview or simulator build is how an operator finds out what is wrong, so it goes.
 *
 * `readinessFailed` is true when any platform's report is not ready OR could not be read at all —
 * a production refusal fails closed, so an outage of the check refuses the build rather than
 * waving it through.
 */
export function buildGateDecision(input: {
  variant: BuildVariant
  readinessFailed: boolean
  overrideReadiness: boolean
}): BuildGateDecision {
  if (!input.readinessFailed) return 'dispatch'
  if (input.variant !== 'production') return 'dispatch-with-warnings'
  return input.overrideReadiness ? 'dispatch-overridden' : 'refused'
}
